import React from "react";
import {
  GDPRProcessorRecommendation,
  GdprSccVendorReviewStatus,
  OrganizationDataRecipientDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { VendorClassificationDto } from "../../../../common/service/server/dto/VendorDto";
import {
  VendorFlagOption,
  VendorRadioOption,
} from "../../../components/get-compliant/vendors/VendorFlagWithDocumentation";
import {
  ClassificationType,
  DisclosureMeta,
} from "../../../components/organisms/data-recipients/DataRecipientDisclosureSettings";
import { ApiRouteHelpers } from "../../../../common/root/apiRoutes";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { VENDOR_CATEGORY_AD_NETWORK } from "../../../types/Vendor";
import { ToolTip } from "@truevault/aquilo";
import { isShopifyAudiences } from "../../../../polaris/util/vendors";
import { ArrowTopRightOnSquareIcon, QuestionMarkCircleIcon } from "@heroicons/react/20/solid";
import { isThirdPartyRecipient } from "../../../util/vendors";
import {
  shouldShowContractFulfillmentSuggestionIcon,
  TruevaultVerifiedBadge,
} from "./VendorDocumentationPage";

export const getVendorClassificationSlug = (
  vendor: OrganizationDataRecipientDto,
): string | null | undefined => {
  // Note that we first check for `classificationId` even though it's not a property of `OrganizationDataRecipientDto`
  // because to update a vendor's classification, we set it's `classificationId` property on the vendor update dto object,
  // which is then converted to the right classification on the backend - but here we still only have access to the local state,
  // which uses `classificationId`
  if ("classificationId" in vendor) {
    return vendor?.classificationOptions?.find((o) => o.id === (vendor as any).classificationId)
      ?.slug;
  }
  return vendor?.classification?.slug;
};

export const getClassificationIdForClassificationType = (
  classificationType: ClassificationType,
  classificationOptions: VendorClassificationDto[],
): string | null => {
  let option = classificationOptions.find((opt) => opt.slug == classificationType);
  if (!option) {
    return null;
  }
  return option.id;
};

export const usClassificationOptionsForVendor = (
  vendor: OrganizationDataRecipientDto,
  disclosureMeta: DisclosureMeta,
): VendorRadioOption<ClassificationType>[] => {
  // The badge marks the option TrueVault's own documentation supports for this vendor (its
  // recommendation). It is shown before anything is selected — it used to decorate whichever
  // option was picked, which read as "TrueVault verified my choice".
  let createDynamicLabelFn = (
    labelText: string,
    isVerifiedOption: (vendor: OrganizationDataRecipientDto) => boolean,
  ) => {
    return (vendor: OrganizationDataRecipientDto, _isSelected: boolean) => {
      if (vendor && vendor.serviceProviderLanguageUrl && isVerifiedOption(vendor)) {
        return (
          <div className="ml-3 flex items-center gap-[6px]">
            <div className="font-medium text-[14px] text-gray-700">{labelText}</div>
            <span title="TrueVault found language supporting this in the vendor's documentation">
              <TruevaultVerifiedBadge />
            </span>
            <div className="flex items-center gap-[4px]">
              <a
                href={vendor.serviceProviderLanguageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline text-[14px] font-medium"
              >
                View Documentation
              </a>
              <ArrowTopRightOnSquareIcon className="w-[16px] h-[16px] text-blue-600" />
            </div>
          </div>
        );
      }
      return labelText;
    };
  };

  let serviceProviderOption: VendorRadioOption<ClassificationType> = {
    // The documentation is service provider language, so it backs this option unless the
    // vendor is recommended as something else.
    label: createDynamicLabelFn(
      "Service Provider",
      (v) =>
        v.serviceProviderRecommendation !== "THIRD_PARTY" &&
        v.serviceProviderRecommendation !== "CONTRACTOR",
    ),
    dynamic: true,
    value: "service-provider",
    type: "file",
    key: "service-provider",
  };

  let thirdPartyOption: VendorRadioOption<ClassificationType> = {
    label: createDynamicLabelFn(
      "Third Party",
      (v) => v.serviceProviderRecommendation === "THIRD_PARTY",
    ),
    dynamic: true,
    value: "third-party",
    type: "file",
    key: "third-party",
  };

  let contractorOption: VendorRadioOption<ClassificationType> = {
    label: createDynamicLabelFn(
      "Contractor",
      (v) => v.serviceProviderRecommendation === "CONTRACTOR",
    ),
    dynamic: true,
    value: "contractor",
    type: "file",
    key: "contractor",
  };

  if (
    isThirdPartyRecipient(vendor) ||
    (disclosureMeta.lockClassification && vendor.classification?.slug === "third-party")
  ) {
    return [thirdPartyOption];
  }

  return [serviceProviderOption, thirdPartyOption, contractorOption];
};

export type GDPRProcessorRecommendationKey = "processor" | "controller" | "unknown";

export const gdprClassificationOptionsForVendor = (): VendorRadioOption<
  GDPRProcessorRecommendation,
  GDPRProcessorRecommendationKey
>[] => {
  // The badge marks the option TrueVault's own documentation supports for this vendor (its
  // recommendation; the backend accepts the documentation for either). It is shown before
  // anything is selected — it used to decorate whichever option was picked, which read as
  // "TrueVault verified my choice".
  let createDynamicLabelFn = (labelText: string, recommendation: GDPRProcessorRecommendation) => {
    return (vendor: OrganizationDataRecipientDto, _isSelected: boolean) => {
      if (
        vendor &&
        vendor.gdprProcessorLanguageUrl &&
        vendor.gdprProcessorRecommendation === recommendation
      ) {
        return (
          <div className="ml-3 flex items-center gap-[6px]">
            <div className="font-medium text-[14px] text-gray-700">{labelText}</div>
            <span title="TrueVault found language supporting this in the vendor's documentation">
              <TruevaultVerifiedBadge />
            </span>
            <div className="flex items-center gap-[4px]">
              <a
                href={vendor.gdprProcessorLanguageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline text-[14px] font-medium"
              >
                View Documentation
              </a>
              <ArrowTopRightOnSquareIcon className="w-[16px] h-[16px] text-blue-600" />
            </div>
          </div>
        );
      }
      return labelText;
    };
  };

  let processorOption: VendorRadioOption<
    GDPRProcessorRecommendation,
    GDPRProcessorRecommendationKey
  > = {
    label: createDynamicLabelFn("Processor", "Processor"),
    dynamic: true,
    value: "Processor",
    type: "file",
    key: "processor",
  };

  let controllerOption: VendorRadioOption<
    GDPRProcessorRecommendation,
    GDPRProcessorRecommendationKey
  > = {
    label: createDynamicLabelFn("Controller", "Controller"),
    dynamic: true,
    value: "Controller",
    type: "file",
    key: "controller",
  };

  let unknownOption: VendorRadioOption<
    GDPRProcessorRecommendation,
    GDPRProcessorRecommendationKey
  > = {
    value: "Unknown",
    type: "normal",
    label: "I'm not sure how to classify this data recipient",
    key: "unknown",
  };

  return [processorOption, controllerOption, unknownOption];
};

export const caContractOptionsForVendor = (): VendorRadioOption<VendorFlagOption>[] => {
  // The badge marks the option TrueVault's own documentation supports for this vendor. It is
  // pinned to that option and shown before anything is selected — it used to decorate
  // whichever option was picked, which read as "TrueVault verified my choice".
  let createDynamicLabelFn = (labelText: string, isVerifiedOption: boolean) => {
    return (vendor: OrganizationDataRecipientDto, _isSelected: boolean) => {
      if (vendor && vendor.cpraContractLanguageUrl && isVerifiedOption) {
        return (
          <div className="ml-3 flex items-center gap-[6px]">
            <div className="font-medium text-[14px] text-gray-700">{labelText}</div>
            <span title="TrueVault found language supporting this in the vendor's documentation">
              <TruevaultVerifiedBadge />
            </span>
            <div className="flex items-center gap-[4px]">
              <a
                href={vendor.cpraContractLanguageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline text-[14px] font-medium"
              >
                View Documentation
              </a>
              <ArrowTopRightOnSquareIcon className="w-[16px] h-[16px] text-blue-600" />
            </div>
          </div>
        );
      }
      return labelText;
    };
  };

  return [
    {
      label: createDynamicLabelFn("CA Contract Requirement", true),
      dynamic: true,
      value: "ca-contract-requirement",
      type: "file",
      key: "ca-contract-requirement",
    },
  ];
};

export const usClassificationHelpText = (
  vendor: OrganizationDataRecipientDto,
  disclosureMeta: DisclosureMeta,
) => {
  const helpLink = (
    <a
      className={"text-primary-600 underline font-medium"}
      href="https://knowledge.truevault.com/article/294-identifying-service-provider-language"
      target="_blank"
      rel="noopener noreferrer"
    >
      Learn more about determining service provider status
    </a>
  );

  if (isThirdPartyRecipient(vendor)) {
    return null;
  }

  if (disclosureMeta.lockClassification) {
    return (
      <>
        Based on your Vendor Settings, this recipient is a Third party to your business. {helpLink}
      </>
    );
  } else if (vendor.serviceProviderLanguageUrl) {
    return <>US Classification is met and verified.</>;
  } else {
    return (
      <>
        Are they considered a service provider or a third party under U.S. privacy laws. {helpLink}
      </>
    );
  }
};

export const gdprClassificationHelpText = (vendor: OrganizationDataRecipientDto) => {
  if (
    vendor.gdprProcessorRecommendation === "Processor" ||
    vendor.gdprProcessorRecommendation === "Controller"
  ) {
    return <>GDPR Classification is met and verified.</>;
  }

  return <>Are they considered a processor or controller under GDPR.</>;
};

export const gppSignalHelpText = (vendor: OrganizationDataRecipientDto) => {
  if (vendor.gppSignalVerifiedStatus === "GPP_SIGNAL_RESPECTED") {
    return (
      <>
        <span className="underline">Global Privacy Protocol</span> signal is respected and verified.
      </>
    );
  } else if (vendor.gppSignalVerifiedStatus === "GPP_ALTERNATE_OPT_OUT_MECHANISM") {
    return <>Uses an alternate opt-out mechanism.</>;
  } else if (vendor.gppSignalVerifiedStatus === "GPP_SIGNAL_NOT_RESPECTED_OR_UNKNOWN") {
    return (
      <>
        <span className="underline">Global Privacy Protocol</span> signal is not respected.
      </>
    );
  }

  return (
    <>
      If a vendor has not agreed to respect browser opt-outs, your website setup instructions will
      be updated to block data sharing with the vendor.
    </>
  );
};

export const internationalDataTransferOptionsForVendor = (
  vendor: OrganizationDataRecipientDto,
  org: OrganizationDto,
  openSafeCountriesDialog: () => void,
): VendorRadioOption<GdprSccVendorReviewStatus, GdprSccVendorReviewStatus>[] => {
  return [
    {
      value: "DPF_LIST",
      type: "normal",
      dynamic: true,
      label: (recipient) => {
        return (
          <div className="ml-3 flex items-center">
            <div className="font-medium text-[14px] text-gray-700 mr-[12px]">
              {recipient.name} is on the DPF list
            </div>
            <ToolTip
              text={
                <>
                  You can search the{" "}
                  <a
                    className="tooltip-link"
                    href="https://www.dataprivacyframework.gov/list"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    DPF participant list
                  </a>{" "}
                  for your vendor.
                </>
              }
              width="220px"
            >
              <QuestionMarkCircleIcon
                className="w-[16px] h-[16px] text-neutral-600 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open("https://www.dataprivacyframework.gov/list", "_blank");
                }}
              />
            </ToolTip>
          </div>
        );
      },
      key: "DPF_LIST",
    },
    {
      value: "DOCS_PROVIDED",
      type: "file",
      disabled: false,
      label: `SCC found for ${vendor.name}`,
      key: "DOCS_PROVIDED",
      createFileLink: (recipient) =>
        ApiRouteHelpers.storage.vendorAgreements.byType(org.id, recipient.vendorId, "scc"),
    },
    {
      value: "FULFILLMENT_ONLY",
      type: "normal",
      dynamic: true,
      disabled: vendor.category === VENDOR_CATEGORY_AD_NETWORK,
      disabledTooltip: "This option does not apply to this type of vendor.",
      label: (recipient) => {
        return (
          <div className="ml-3 flex items-center">
            <div className="font-medium text-[14px] text-gray-700 mr-[12px]">
              Vendor is used for contract fulfillment only
            </div>
            <ToolTip
              text="When necessary to perform a contract with the consumer, such as fulfilling an order, the data transfer rules do not apply to the vendor receiving the data in a foreign country."
              width="220px"
            >
              <QuestionMarkCircleIcon className="w-[16px] h-[16px] text-neutral-600" />
            </ToolTip>
            {shouldShowContractFulfillmentSuggestionIcon(recipient) && (
              <ToolTip
                text="Vendors marked with this icon have been flagged by our team as not requiring documentation."
                width="220px"
              >
                <img
                  className="ml-xs"
                  src="/assets/images/icon-lightbulb.svg"
                  color="primary"
                  width="16"
                  style={{ marginBottom: "1px" }} // hack for better centered vertical alignment in the row
                />
              </ToolTip>
            )}
          </div>
        );
      },
      key: "FULFILLMENT_ONLY",
    },
    {
      value: "SAFE_COUNTRIES_ONLY",
      type: "normal",
      dynamic: true,
      label: (_recipient) => {
        return (
          <div className="ml-3 text-[14px] text-gray-700 font-medium">
            This vendor processes data only in{" "}
            <span
              className="text-weight-medium cursor-pointer text-decoration-underline color-secondary-600"
              onClick={openSafeCountriesDialog}
            >
              safe countries
            </span>
          </div>
        );
      },
      key: "SAFE_COUNTRIES_ONLY",
    },
  ];
};

export const isVendorServiceClassificationLockedToThirdParty = (
  vendor: OrganizationDataRecipientDto,
) => {
  const autoSelectThirdParty = Boolean(
    vendor.ccpaIsSelling ||
      vendor.isAlwaysDataSellingVendor ||
      vendor.ccpaIsSharing ||
      vendor.usesCustomAudience,
  );

  return isShopifyAudiences(vendor) || autoSelectThirdParty;
};
